import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { SharedKernel } from '@/shell';
import type { ApiError, StoredMethodType } from '@neon-one/auctions-types';

import { PAYMENT_METHOD_TOKEN_FIELD } from '@neon-one/auctions-types';

import { activationApi } from './api';
import {
  isPaymentMethodDeclined,
  paymentMethodDeclineMessage,
  useSession,
  useAuctionEvent,
} from '@/shell';

export type Kernel = SharedKernel;

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface ActivationDraft {
  firstName: string;
  lastName: string;
  phone: string;
  paymentMethodToken: string;
  // GT1-488 — the rail the token was minted on. PATCH /me is the activation
  // tokenization path, so without this an ACH token stored here lands as
  // 'card' server-side.
  paymentMethodType: StoredMethodType | null;
  smsOptIn: boolean;
  emailOptIn: boolean;
  tcAccepted: boolean;
}

export const useActivationStore = defineStore('activation', () => {
  const draft = ref<ActivationDraft>({
    firstName: '',
    lastName: '',
    phone: '',
    paymentMethodToken: '',
    paymentMethodType: null,
    smsOptIn: false,
    emailOptIn: false,
    tcAccepted: false,
  });
  const state = ref<SubmitState>('idle');
  const fieldErrors = ref<Record<string, string[]>>({});
  const formError = ref<string | null>(null);
  // Bumped whenever a captured token is invalidated server-side.
  // The card field keys off this: its hosted iframe cannot be re-mounted into
  // a re-rendered container (the SDK short-circuits an already-initialised
  // field), so clearing the token alone would leave a blank, unusable field
  // and no way back to card entry.
  const paymentFieldEpoch = ref(0);

  function hydrateFromSession(): void {
    const bidder = useSession().bidder;
    if (!bidder) return;
    draft.value = {
      ...draft.value,
      firstName: bidder.firstName,
      lastName: bidder.lastName,
      phone: bidder.phone ?? '',
      tcAccepted: bidder.tcAcceptedAt != null,
      // Every opening is a fresh offer: a box ticked before a cancel must not
      // reappear pre-checked and be submitted as consent the bidder did not
      // just give.
      emailOptIn: false,
      smsOptIn: false,
    };
  }

  const missingFields = computed<readonly string[]>(() => {
    const bidder = useSession().bidder;
    const event = useAuctionEvent().event;
    if (!bidder) return [];
    const missing: string[] = [];
    // Phone is NOT an activation requirement and must not be listed here.
    // The server activates on payment + terms + first/last name only
    // (bidderRegistration.ts `shouldActivate`), and every registration path
    // takes phone as optional — it is mandatory only alongside an SMS opt-in.
    // Listing it made the gate block a bid the server would have accepted.
    const paymentRequired = event?.paymentMethodRequirement === 'required';
    if (!bidder.hasPaymentMethod && paymentRequired) missing.push('paymentMethod');
    if (!bidder.tcAcceptedAt) missing.push('tcAcceptance');
    return missing;
  });

  function update<K extends keyof ActivationDraft>(field: K, value: ActivationDraft[K]): void {
    draft.value[field] = value;
    if (fieldErrors.value[field]) {
      const next = { ...fieldErrors.value };
      delete next[field as string];
      fieldErrors.value = next;
    }
    if (formError.value) formError.value = null;
  }

  function reset(): void {
    state.value = 'idle';
    fieldErrors.value = {};
    formError.value = null;
  }

  // Keeps token + declared rail in lockstep — the card field and the bank
  // field both route through here rather than each setting the token alone.
  function setPaymentMethod(token: string, methodType: StoredMethodType | null): void {
    draft.value.paymentMethodToken = token;
    draft.value.paymentMethodType = token ? methodType : null;
    if (fieldErrors.value.paymentMethodToken) {
      const next = { ...fieldErrors.value };
      delete next.paymentMethodToken;
      fieldErrors.value = next;
    }
    if (formError.value) formError.value = null;
  }

  // The declined token authorized nothing and is spent server-side, so it is
  // dropped rather than re-submitted. Order matters: setPaymentMethod clears
  // the token's field error, so the reason is written after it.
  function applyPaymentMethodDecline(message: string): void {
    setPaymentMethod('', null);
    paymentFieldEpoch.value += 1;
    fieldErrors.value = { [PAYMENT_METHOD_TOKEN_FIELD]: [message] };
  }

  async function submit(): Promise<boolean> {
    state.value = 'submitting';
    fieldErrors.value = {};
    formError.value = null;
    const payload = {
      firstName: draft.value.firstName || undefined,
      lastName: draft.value.lastName || undefined,
      phone: draft.value.phone || null,
      paymentMethodToken: draft.value.paymentMethodToken || undefined,
      paymentMethodType: draft.value.paymentMethodToken
        ? draft.value.paymentMethodType ?? undefined
        : undefined,
      smsOptIn: draft.value.smsOptIn || undefined,
      emailOptIn: draft.value.emailOptIn || undefined,
      tcAccepted: draft.value.tcAccepted || undefined,
    };
    try {
      const bidder = await activationApi.patchMe(payload);
      useSession().refreshFromBidder(bidder);
      state.value = 'success';
      return true;
    } catch (e) {
      const err = e as ApiError;
      state.value = 'error';
      // A refused card means no method was stored and the bidder is still one
      // card away from bidding, so the drawer stays open on the reason with a
      // fresh card field rather than closing on a toast.
      if (isPaymentMethodDeclined(err)) {
        applyPaymentMethodDecline(paymentMethodDeclineMessage(err));
        return false;
      }
      if (err.code === 'VALIDATION_FAILED' && err.fieldErrors) {
        fieldErrors.value = err.fieldErrors;
      } else {
        formError.value = 'We couldn’t save your details. Try again.';
      }
      return false;
    }
  }

  return {
    draft,
    state,
    fieldErrors,
    formError,
    paymentFieldEpoch,
    missingFields,
    hydrateFromSession,
    update,
    reset,
    setPaymentMethod,
    submit,
  };
});
